<template>
  <v-form ref="formRef"
          v-model="valid"
          :lazy-validation="lazyValidation"
          @submit.prevent>
    <v-container fluid :class="{ 'ma-0 pa-0' : dense}">
      <v-row dense>
        <v-col v-for="(field, index) in visibleFields"
               :key="field.key || field.model || index"
               :class="`${field.topSticky ? 'sticky-field' : ''} ${field.colClass || ''}`"
               :cols="field.cols || '12'"
               :sm="field.sm"
               :md="field.md"
               :lg="field.lg"
               :xl="field.xl"
               :style="field.topSticky ? { top: field.topSticky } : {}"
               @click.stop="onSelectedField(field)">
          <div>
            <component :is="field.component || 'dynamic-text-field'"
                       v-show="fieldIsVisible(field)"
                       :id="'component' + index"
                       :ref="el => setFieldRef(el, index)"
                       :tabindex="index"
                       :field="field"
                       :read-only="readOnly"
                       :box="box"
                       :small="small"
                       :value="formValue"
                       :item-key="itemKey"
                       :is-validating="isValidating"
                       @extract-data="onExtractData"
                       @file-selected="onFileSelected"
                       @document-deleted="onDocumentDeleted"
                       @request-sign="onRequestSign"
                       @input="onFieldValueChange" />
          </div>
          <v-divider v-if="showDivider" />
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import { defineComponent, ref, computed, watch, nextTick, getCurrentInstance } from 'vue'
import { cloneDeep, filter, get, set, each, isEmpty } from 'lodash'
import Store from '~store/store'

import DynamicTextField from './DynamicComponents/DynamicTextField.vue'
import DynamicPasswordField from './DynamicComponents/DynamicPasswordField.vue'
import DynamicRangeSlider from './DynamicComponents/DynamicRangeSlider.vue'
import DynamicNumberField from './DynamicComponents/DynamicNumberField.vue'
import DynamicSelect from './DynamicComponents/DynamicSelect.vue'
import DynamicAddress from './DynamicComponents/DynamicAddress.vue'
import DynamicRadioGroup from './DynamicComponents/DynamicRadioGroup.vue'
import DynamicChips from './DynamicComponents/DynamicChips.vue'
import DynamicCheckBox from './DynamicComponents/DynamicCheckBox.vue'
import DynamicCheckBoxGroup from './DynamicComponents/DynamicCheckBoxGroup.vue'
import DynamicDateField from './DynamicComponents/DynamicDateField.vue'
import DynamicDivider from './DynamicComponents/DynamicDivider.vue'
import DynamicPhotoField from './DynamicComponents/DynamicPhotoField.vue'
import DynamicFileField from './DynamicComponents/DynamicFileField.vue'
import DynamicSimpleFileField from './DynamicComponents/DynamicSimpleFileField.vue'
import DynamicGeneratedFileField from './DynamicComponents/DynamicGeneratedFileField.vue'
import DynamicColorSelect from './DynamicComponents/DynamicColorSelect.vue'
import DynamicTextArea from './DynamicComponents/DynamicTextArea.vue'
import DynamicUserSelector from './DynamicComponents/DynamicUserSelector.vue'
import DynamicAsyncSelector from './DynamicComponents/DynamicAsyncSelector.vue'
import DynamicPostalCode from './DynamicComponents/DynamicPostalCode.vue'
import DynamicMonthYearField from './DynamicComponents/DynamicMonthYearField.vue'

export default defineComponent({
  name: 'RegDynamicForm',
  components: {
    'dynamic-text-field': DynamicTextField,
    'dynamic-password-field': DynamicPasswordField,
    'dynamic-range-slider': DynamicRangeSlider,
    'dynamic-number-field': DynamicNumberField,
    'dynamic-select': DynamicSelect,
    'dynamic-address': DynamicAddress,
    'dynamic-radio-group': DynamicRadioGroup,
    'dynamic-chips': DynamicChips,
    'dynamic-checkbox': DynamicCheckBox,
    'dynamic-checkbox-group': DynamicCheckBoxGroup,
    'dynamic-date-field': DynamicDateField,
    'dynamic-divider': DynamicDivider,
    'dynamic-photo-field': DynamicPhotoField,
    'dynamic-file-field': DynamicFileField,
    'dynamic-simple-file-field': DynamicSimpleFileField,
    'dynamic-generated-file-field': DynamicGeneratedFileField,
    'dynamic-color-select': DynamicColorSelect,
    'dynamic-text-area': DynamicTextArea,
    'dynamic-user-selector': DynamicUserSelector,
    'dynamic-async-selector': DynamicAsyncSelector,
    'dynamic-postal-code': DynamicPostalCode,
    'dynamic-month-year-field': DynamicMonthYearField
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    fields: {
      required: true,
      type: Array
    },
    lazyValidation: {
      type: Boolean,
      default: true
    },
    enableFormEditor: {
      type: Boolean,
      default: false
    },
    showDivider: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    hideEmpty: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    },
    dense: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'selected-field', 'file-selected', 'document-deleted', 'extract-data', 'request-sign'],
  setup(props, { emit, expose }) {
    const instance = getCurrentInstance()
    const $t = (key) => instance.proxy.$t(key)
    const $vuetify = instance.proxy.$vuetify
    const $confirm = instance.proxy.$confirm

    const valid = ref(true)
    const formValue = ref({})
    const isValidating = ref(false)
    const formRef = ref(null)
    const fieldRefs = ref({})
    let lastEmittedValue = null // Guarda última referência emitida para detectar loops

    const visibleFields = computed(() => {
      const visible = filter(props.fields, field => fieldIsVisible(field))

      if (props.hideEmpty) {
        return filter(visible, field => {
          const fieldValue = get(formValue.value, field.model)
          return fieldValue || !isEmpty(fieldValue)
        })
      }

      return visible
    })

    const setFieldRef = (el, index) => {
      if (el) {
        fieldRefs.value[index] = el
      } else {
        delete fieldRefs.value[index]
      }
    }

    const startUpload = () => {
      visibleFields.value.forEach((field, i) => {
        if (['dynamic-file-field', 'dynamic-photo-field', 'dynamic-simple-file-field'].includes(field.component)) {
          const ref = fieldRefs.value[i]
          if (ref?.startUpload) {
            ref.startUpload()
          }
        }
      })
    }

    const cancelUpload = () => {
      visibleFields.value.forEach((field, i) => {
        if (['dynamic-file-field', 'dynamic-photo-field', 'dynamic-simple-file-field'].includes(field.component)) {
          const ref = fieldRefs.value[i]
          if (ref?.cancelUpload) {
            ref.cancelUpload()
          }
        }
      })
    }

    const onFormValueChange = (newVal) => {
      // Se for a mesma referência que acabamos de emitir, ignora (previne loop e re-clonagem)
      if (newVal === lastEmittedValue) {
        return
      }

      formValue.value = cloneDeep(newVal)
      each(props.fields, field => {
        if (field.dataSource && field.dataSource.actionName) {
          return Store.dispatch(field.dataSource.actionName)
        }
      })
    }

    const onFieldValueChange = (field, newValue) => {
      if (field.model) {
        const newFormValue = cloneDeep(formValue.value)
        set(newFormValue, field.model, newValue)

        if (field.modelMap) {
          Object.keys(field.modelMap).forEach(key => {
            set(newFormValue, field.modelMap[key], newValue[key])
          })
        }

        // 1. Atualiza formValue internamente PRIMEIRO (evita re-renderização)
        formValue.value = newFormValue

        // 2. Guarda referência para prevenir loop quando pai devolver
        lastEmittedValue = newFormValue

        // 3. Emite para o pai (ele pode devolver, mas será ignorado pelo watch)
        emit('input', newFormValue)
      }
    }

    const fieldIsVisible = (field) => {
      if (field.visible === undefined) {
        return true
      } else if (typeof field.visible === 'function') {
        return field.visible(formValue.value)
      } else {
        return Boolean(field.visible)
      }
    }

    const onSelectedField = (field) => {
      emit('selected-field', field)
    }

    const confirmMissingDocuments = () => {
      return new Promise((resolve) => {
        const missingDocuments = []
        let firstFieldWithError

        visibleFields.value.forEach((field, i) => {
          if (['dynamic-file-field', 'dynamic-photo-field', 'dynamic-simple-file-field'].includes(field.component)) {
            const ref = fieldRefs.value[i]
            if (ref?.validate) {
              const valid = ref.validate()
              if (!valid) {
                firstFieldWithError = ref
                missingDocuments.push(field.label)
              }
            }
          }
        })

        if (!missingDocuments.length) {
          resolve(true)
          return
        }

        $confirm({
          title: 'Existem Documentos Pendentes',
          message: '- ' + missingDocuments.join(',<br/>- ') + '<br><br>Deseja continuar mesmo assim?',
          confirmText: $t('yes'),
          cancelText: $t('no')
        }).then((confirmed) => {
          if (confirmed) {
            resolve(true)
          } else {
            const parentElement = document.getElementsByClassName('side-bar-content')?.[0]
            if (firstFieldWithError) {
              $vuetify.goTo(firstFieldWithError, {
                duration: 300,
                offset: 150,
                easing: 'easeInOutCubic',
                container: parentElement
              })
            }
            resolve(false)
          }
        })
      })
    }

    const resetValidation = () => {
      isValidating.value = false
      if (formRef.value) {
        formRef.value.resetValidation()
      }
    }

    const validate = (scrollInvalid = true, inferredParent) => {
      isValidating.value = true
      return new Promise((resolve) => {
        nextTick(async () => {
          if (!formRef.value) {
            isValidating.value = false
            resolve(true)
            return
          }

          const isValid = await formRef.value.validate()

          if (isValid) {
            isValidating.value = false
            resolve(true)
          } else {
            if (scrollInvalid) {
              let firstFieldWithError

              visibleFields.value.every((field, i) => {
                const comp = fieldRefs.value[i]
                // Check if component has validation error
                // First try to call validate method if available
                if (comp?.validate && typeof comp.validate === 'function') {
                  const valid = comp.validate()
                  if (!valid) {
                    firstFieldWithError = comp
                    return false
                  }
                }
                // Fallback to hasError property
                else if (comp?.hasError) {
                  firstFieldWithError = comp
                  return false
                }
                return true
              })

              if (firstFieldWithError) {
                let parentElement = inferredParent || document.getElementsByClassName('side-bar-content')?.[0]
                if (!parentElement) {
                  parentElement = firstFieldWithError.$el?.parentElement
                }
                $vuetify.goTo(firstFieldWithError, {
                  duration: 300,
                  offset: 150,
                  easing: 'easeInOutCubic',
                  container: parentElement
                })
              }
            }
            // Don't reset isValidating when validation fails
            // Keep showing validation errors until user fixes them or cancels
            resolve(false)
          }
        })
      })
    }

    const scrollToField = (model, inferredParent) => {
      let fieldRef

      visibleFields.value.every((field, i) => {
        const comp = fieldRefs.value[i]
        if (comp?.model === model) {
          fieldRef = comp
          return false
        }
        return true
      })

      if (fieldRef) {
        let parentElement = inferredParent || document.getElementsByClassName('side-bar-content')?.[0]
        if (!parentElement) {
          parentElement = fieldRef.$el?.parentElement
        }
        $vuetify.goTo(fieldRef, {
          duration: 300,
          offset: 150,
          easing: 'easeInOutCubic',
          container: parentElement
        })
      }
    }

    const scrollTop = (inferredParent) => {
      let parentElement = inferredParent || document.getElementsByClassName('side-bar-content')?.[0]
      if (!parentElement) {
        parentElement = instance.proxy.$el
      }
      $vuetify.goTo(0, {
        duration: 300,
        offset: 150,
        easing: 'easeInOutCubic',
        container: parentElement
      })
    }

    const reset = () => {
      isValidating.value = false
      return formRef.value?.reset()
    }

    const onFileSelected = (file) => {
      emit('file-selected', file)
    }

    const onDocumentDeleted = (docId) => {
      emit('document-deleted', docId)
    }

    const onExtractData = (result, documentId) => {
      emit('extract-data', result, documentId)
    }

    const onRequestSign = (e) => {
      emit('request-sign', e)
    }

    watch(() => props.value, onFormValueChange, { immediate: true, deep: true })

    // Expose methods for parent components
    expose({
      startUpload,
      cancelUpload,
      confirmMissingDocuments,
      resetValidation,
      validate,
      scrollToField,
      scrollTop,
      reset
    })

    return {
      valid,
      formValue,
      isValidating,
      formRef,
      visibleFields,
      setFieldRef,
      fieldIsVisible,
      onSelectedField,
      onFieldValueChange,
      onFileSelected,
      onDocumentDeleted,
      onExtractData,
      onRequestSign
    }
  }
})
</script>

<style lang="scss">
.reg-input .v-input-group__details {
  display: block;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.reg-dynamic-form-handler {
  display: table-cell;
  min-width: 50px;
  text-align: center;
  vertical-align: middle;
  cursor: move !important;
  cursor: -webkit-grabbing !important;
}

.reg-dynamic-form-comp {
  display: table-cell;
  width: 100%;
  vertical-align: middle;
}

.sticky-field {
  position: sticky;
  z-index: 2;
  background-color: #FFFFFF !important;
}
</style>
